import React, { useEffect, useState } from 'react';
import { cn } from '@newcore/ui/cn';
import { FontWeights } from '@newcore/ui/enums/font-weights';
import { TextSizes } from '@newcore/ui/enums/typography-text-sizes';
import { Spinner } from '@newcore/ui/loading';
import { Text } from '@newcore/ui/typography';
import { textVariants } from '@newcore/ui/variants/typography-text-variants';
import { QRCodeSVG } from 'qrcode.react';
import type { PairingData } from '@/hooks/use-device-pairing';
import { formatPairingCode } from '@/utils/pairing-utils';

interface PairingActiveStateProps {
  pairingData: PairingData;
  /** Payload encoded into the QR - a pairing URL, or the raw code as fallback. */
  qrValue: string;
}

/**
 * Active state - shows QR code and pairing code
 */
export const PairingActiveState: React.FC<PairingActiveStateProps> = ({
  pairingData,
  qrValue,
}): React.ReactElement => {
  const [copied, setCopied] = useState(false);
  const formattedCode = formatPairingCode(pairingData.pairingCode);

  // Auto-reset copied state with proper cleanup
  useEffect(() => {
    if (!copied) {
      return;
    }

    const timer = setTimeout(() => setCopied(false), 2000);

    return () => clearTimeout(timer);
  }, [copied]);

  const handleCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(pairingData.pairingCode);
      setCopied(true);
    } catch {
      // Clipboard API not available - fail silently
    }
  };

  return (
    <>
      {/* QR Code */}
      <div className='flex flex-col items-center space-y-3'>
        <div className='rounded-lg border-2 border-secondary p-3 bg-white'>
          <QRCodeSVG
            value={qrValue}
            size={180}
            level='M'
            role='img'
            aria-label={`QR code for pairing code ${formattedCode}`}
          />
        </div>
      </div>

      {/* Manual Code Entry - Click to copy */}
      <div className='flex flex-col items-center gap-2'>
        <Text as='p' className='text-tertiary'>
          Or enter this code manually:
        </Text>
        <button
          type='button'
          onClick={handleCopy}
          className={cn(
            textVariants({ size: TextSizes.XL, weight: FontWeights.BOLD }),
            'rounded-lg bg-secondary px-4 py-2 font-mono tracking-wider cursor-pointer',
            'hover:bg-secondary/90 active:scale-95 transition-all',
            copied && 'ring-2 ring-utility-green-500 bg-utility-green-50'
          )}
          aria-label={
            copied ? 'Copied to clipboard' : 'Click to copy pairing code'
          }
          data-testid='pairing-code-button'
        >
          {formattedCode}
        </button>
        {copied && (
          <Text
            size={TextSizes.XS}
            className='text-utility-green-600'
            aria-live='polite'
          >
            Copied!
          </Text>
        )}
      </div>

      {/* Polling Indicator */}
      <div className='flex items-center justify-center gap-2 text-tertiary'>
        <Spinner />
        <Text>Waiting for device...</Text>
      </div>
    </>
  );
};

PairingActiveState.displayName = 'PairingActiveState';
